import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { inject, observer } from "mobx-react"
import React from "react"
import Button from "react-bootstrap/Button"
import Col from "react-bootstrap/Col"
import Form from "react-bootstrap/Form"
import Row from "react-bootstrap/Row"
import { withTranslation, WithTranslation } from "react-i18next"
import { MerchantAdminApi } from "../Api"
import { CreateOrganizationLocation, HomeLocation, ManagePremiumLocation } from "../Locations"
import { IOrganization } from "../models/Organization"
import { IUserSessionStore } from "../models/UserSessionStore"

const filterOrganizations = (organizations: IOrganization[], searchTerm: string, showHidden: boolean): IOrganization[] => {
  let filtered = organizations

  // First apply visibility filter
  if (!showHidden) {
    filtered = filtered.filter(organization => {
      // Get visible venues (not hidden)
      const visibleVenues = organization.venues.filter(venue => !venue.isHidden)
      // Only show organizations that have at least one visible venue
      return visibleVenues.length > 0
    })
  }

  // Then apply search filter
  if (searchTerm.trim()) {
    const lowercaseSearch = searchTerm.toLowerCase()

    filtered = filtered.filter(organization => {
      // Search in organization name
      if (organization.name?.toLowerCase().includes(lowercaseSearch)) {
        return true
      }

      // Search in venues (consider visibility filter)
      const venuesToSearch = showHidden ? organization.venues : organization.venues.filter(venue => !venue.isHidden)

      return venuesToSearch.some(venue => {
        // Search in venue name
        if (venue.name?.toLowerCase().includes(lowercaseSearch)) {
          return true
        }

        // Search in venue description (LocalizableString)
        if (venue.description) {
          const description = venue.description.getTranslation()
          if (description.toLowerCase().includes(lowercaseSearch)) {
            return true
          }
        }
        // Search in venue location cities (from venue.locations[].city)
        if (venue.locations) {
          return venue.locations.some(location => {
            return location.city?.toLowerCase()?.includes(lowercaseSearch) ?? false
          })
        }

        return false
      })
    })
  }

  return filtered
}

export interface ChangeOrgScreenProps extends WithTranslation {
  api?: MerchantAdminApi
  userSessionStore?: IUserSessionStore
  history?: any
}

interface ChangeOrgScreenState {
  searchTerm: string
  showHidden: boolean
}

@inject("api")
@inject("userSessionStore")
@observer
class ChangeOrgScreen extends React.Component<ChangeOrgScreenProps, ChangeOrgScreenState> {
  constructor(props: ChangeOrgScreenProps) {
    super(props)
    this.state = {
      searchTerm: "",
      showHidden: false
    }
  }
  onClick(organization: IOrganization) {
    this.props.userSessionStore!.setSelectedOrganization(organization)
    this.props.history.push(HomeLocation.toUrl(this.props.userSessionStore!.selectedBranch!.urlFields()))
  }

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchTerm: event.target.value })
  }

  clearSearch = () => {
    this.setState({ searchTerm: "" })
  }

  toggleShowHidden = () => {
    this.setState(prev => ({ showHidden: !prev.showHidden }))
  }

  render() {
    const { t, userSessionStore } = this.props
    const { searchTerm, showHidden } = this.state
    const filteredOrganizations = filterOrganizations(userSessionStore!.organizations, searchTerm, showHidden)

    return (
      <div className="container-fluid">
        <Row>
          <Col xs={12} className="mt-4">
            <h5>{t("changeOrg")}</h5>
          </Col>
        </Row>

        {userSessionStore!.isSuperAdmin &&
          <Row className="justify-content-end">
            <Col lg={12}>
              <Button
                onClick={() => this.props.history.push(CreateOrganizationLocation.toUrl())}
                style={{ float: 'right', paddingLeft: 10, paddingRight: 10 }}
                variant="primary"
                className="font-weight-bold">
                {t("superAdminScreen.addNewOrganization.button")}
              </Button>

              <Button
                onClick={() => this.props.history.push(ManagePremiumLocation.toUrl())}
                style={{ float: 'right', paddingLeft: 10, paddingRight: 10, marginRight: 10 }}
                variant="primary"
                className="font-weight-bold">
                {t("superAdminScreen.addNewOrganization.managePremium")}
              </Button>
            </Col>
          </Row>
        }

        {userSessionStore!.organizations.length > 5 && (
          <Row className="mt-4">
            <Col xs={12}>
              <div style={{ backgroundColor: '#2a2a2a', padding: '16px', borderRadius: '8px', border: '1px solid #444' }}>
                <Form.Group className="mb-0 d-flex align-items-center">
                  <Form.Control
                    type="text"
                    placeholder={t("search", "Search organizations...")}
                    value={searchTerm}
                    onChange={this.handleSearchChange}
                    className="flex-grow-1"
                  />
                  {searchTerm && (
                    <Button
                      variant="outline-light"
                      style={{ border: '0px' }}
                      className="ms-2"
                      onClick={this.clearSearch}
                      title={t("clearSearch", "Clear search")}
                    >
                      <FontAwesomeIcon icon={["fas", "times"]} />
                    </Button>
                  )}

                  <div className="ms-3 ml-3 d-flex align-items-center flex-shrink-0" style={{ gap: '0.5rem' }}>
                    <Form.Label htmlFor="showHidden" className="text-light mb-0 me-2 text-nowrap">
                      <FontAwesomeIcon icon={['fal', 'eye-slash']} className="mr-1" />
                    </Form.Label>
                    <Form.Check
                      type="checkbox"
                      id="showHidden"
                      checked={showHidden}
                      onChange={this.toggleShowHidden}
                      className="mb-0"
                    />
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>
        )
        }

        <Row className="mt-4">
          <Col xs={12} className="d-flex flex-column">
            {filteredOrganizations.map((organization) => {
              let isCurrent = userSessionStore!.currentOrganization!.id === organization.id
              return (
                <div key={organization.id} className="d-flex align-items-stretch justify-content-between mb-3 p-3 flex-fill organization-card"
                  style={{
                    borderBottom: '1px solid #353535',
                    minHeight: 'fit-content'
                  }}
                  onClick={() => this.onClick(organization)}>
                  <div>
                    <h5 className="mb-0">
                      {organization.name}
                      {isCurrent ? <FontAwesomeIcon className="ml-2" icon={["fas", "check"]} /> : null}
                    </h5>
                    <div className=" mt-1">
                      {organization.venues
                        .filter(venue => showHidden || !venue.isHidden)
                        .map((venue) => (
                          <div key={venue.id} style={{
                            fontSize: '0.8rem',
                            fontStyle: venue.isHidden ? 'italic' : 'normal'
                          }}
                            className="small text-secondary">
                            {venue.isHidden && <FontAwesomeIcon icon={['fal', 'eye-slash']} className="mr-1" />}
                            {venue.name}
                          </div>
                        ))}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="text-secondary text-right">{organization.countryId.name}</div>
                    {!organization.isPremium && (
                      <div className="badge bg-secondary text-dark me-3">{t("changeOrgScreen.freemium", "Freemium")}</div>
                    )}
                  </div>
                </div>
              )
            })}
            {filteredOrganizations.length === 0 && searchTerm.trim() && (
              <div className="text-center text-muted mt-3">
                <div className="mb-2">
                  {t("changeOrgScreen.noSearchResultsFound", "No organizations found matching your search.")}
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={this.clearSearch}
                >
                  {t("changeOrgScreen.clearSearch", "Clear search")}
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </div >
    )
  }
}

export default withTranslation()(ChangeOrgScreen)
